import React, { useContext } from "react";
import { Box, Typography, Button } from "@mui/material";
import { styled } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { firestore } from "../firebaseConfig";
import FavoriteIcon from "@mui/icons-material/Favorite";
import TwitterIcon from "@mui/icons-material/Twitter";
import UserContext from "../context/userContext";
import GlobalStateContext from "../context/globalStateContext";
import AuthModal from "../components/AuthModal";

// Using exact styling from DogProfilePage
const PageContainer = styled(Box)({
  width: "100vw",
  maxWidth: "100%",
  height: "100vh",
  maxHeight: "100vh",
  backgroundColor: "#000000", // EXACT: Pure black background
  color: "#FFFFFF",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  "&::-webkit-scrollbar": {
    display: "none",
  },
});

const Header = styled(Box)({
  width: "100%",
  padding: "16px 24px",
});

const HeaderContent = styled(Box)({
  maxWidth: "800px",
  margin: "0 auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const Logo = styled(Box)({
  display: "flex",
  alignItems: "center",
  fontSize: "18px",
  fontWeight: "600",
  color: "#FFFFFF",
  gap: "8px",
  cursor: "pointer",
});

const NavItems = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "24px",
});

const NavLink = styled(Typography)({
  fontSize: "14px",
  color: "#9CA3AF",
  cursor: "pointer",
  fontWeight: "400",
  "&:hover": {
    color: "#FFFFFF",
  },
});

const MainContainer = styled(Box)({
  maxWidth: "900px",
  margin: "0 auto",
  padding: "0 24px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  flex: 1,
  textAlign: "center",
  height: "100%",
  overflow: "hidden",
});

const MissionTitle = styled(Typography)({
  fontSize: "36px",
  fontWeight: "700",
  color: "#FFFFFF",
  marginBottom: "24px",
  lineHeight: "1.1",
});

const MissionSubtitle = styled(Typography)({
  fontSize: "24px",
  color: "#9CA3AF",
  marginBottom: "48px",
  fontWeight: "400",
  maxWidth: "600px",
});

const BrowseButton = styled(Button)({
  backgroundColor: "#10B981",
  color: "#FFFFFF",
  borderRadius: "10px",
  padding: "16px 32px",
  fontSize: "18px",
  fontWeight: "600",
  textTransform: "none",
  boxShadow: "0 2px 8px rgba(16, 185, 129, 0.25)",
  transition: "all 0.2s ease",
  marginBottom: "64px",
  "&:hover": {
    backgroundColor: "#059669",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.35)",
  },
});

const FounderSection = styled(Box)({
  marginBottom: "40px",
});

const FounderText = styled(Typography)({
  fontSize: "16px",
  color: "#9CA3AF",
  marginBottom: "16px",
  fontWeight: "400",
});

const TwitterButton = styled(Button)({
  backgroundColor: "transparent",
  color: "#9CA3AF",
  border: "1px solid #374151",
  borderRadius: "6px",
  padding: "8px 16px",
  fontSize: "14px",
  fontWeight: "400",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#1F2937",
    color: "#FFFFFF",
  },
});

const Footer = styled(Box)({
  textAlign: "center",
  padding: "32px 24px",
  color: "#6B7280",
  fontSize: "14px",
  maxWidth: "800px",
  margin: "0 auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap",
});

const FooterLink = styled(Typography)({
  fontSize: "14px",
  color: "#9CA3AF",
  cursor: "pointer",
  fontWeight: "400",
  "&:hover": {
    color: "#FFFFFF",
  },
});

const Bullet = styled(Typography)({
  fontSize: "14px",
  color: "#6B7280",
  userSelect: "none",
});

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { globalState, setGlobalState } = useContext(GlobalStateContext);  

  const handleBrowseDogs = async () => {
    try {
      // Fetch all dogs from Firestore
      const querySnapshot = await firestore.collection("web-available-dogs").get();
      
      if (!querySnapshot.empty) {
        const allDogs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Filter for pending status dogs (commented out since all are expired)
        // const pendingDogs = allDogs.filter(dog => dog.euthanasiaStatus === 'pending');
        // const dogsToChooseFrom = pendingDogs.length > 0 ? pendingDogs : allDogs;
        
        // For now, just pick from all dogs since they're all expired
        const dogsToChooseFrom = allDogs;
        
        if (dogsToChooseFrom.length > 0) {
          // Pick a random dog
          const randomDog = dogsToChooseFrom[Math.floor(Math.random() * dogsToChooseFrom.length)];
          navigate(`/${randomDog.slug}`);
        } else {
          console.log('No dogs found');
          navigate('/browse'); // Fallback
        }
      } else {
        console.log('No dogs in collection');
        navigate('/browse'); // Fallback
      }
    } catch (error) {
      console.error('Error fetching dogs:', error);
      navigate('/browse'); // Fallback
    }
  };

  return (
    <PageContainer>
      <Header>
        <HeaderContent>
          <Logo onClick={() => navigate('/')}>
            🍃
            Odessa
          </Logo>
       
        </HeaderContent>
      </Header>
      <MainContainer>
        <MissionTitle>
          Break free from weed dependency
        </MissionTitle>
        
        <MissionSubtitle>
          Track your progress, build healthy habits, and reclaim control of your life.
        </MissionSubtitle>

        <BrowseButton 
          onClick={() => window.open('https://apps.apple.com/app/odessa-quit-weed', '_blank')}
          startIcon={<FavoriteIcon />}
        >
          Download on App Store
        </BrowseButton>

        <FounderSection>
          <FounderText>
            Join thousands taking control of their relationship with cannabis
          </FounderText>
        </FounderSection>
      </MainContainer>

      
      {/* Auth Modal */}
      {globalState.showAuthModal && <AuthModal />}
    </PageContainer>
  );
};

export default LandingPage;